import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  Paper,
  Autocomplete,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Calculate as CalculateIcon,
  Receipt as ReceiptIcon,
  Inventory2 as Inventory2Icon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Remove as RemoveIcon,
  FileUpload as FileUploadIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import {
  useProductosMaterialContext,
  guardarFacturaBackend,
  enviarPayloadFacturaElectronica,
  emitirEcfDgii,
  revertirFacturaRechazada,
} from 'context';
import {
  tienePermisosModulo
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { reglaDescuentoAplicaAProducto } from 'utils/reglasDescuentos';
import ConsultaProducto from 'productosnoserializados/ConsultaProducto';
import { hoyLocal } from 'lib/fechaLocal';
import PagoFactura from './PagoFactura';
import VistaFactura from './VistaFactura';
import tiposDocumentosJson from 'utils/tiposDocumentos.json';

/** Clave del programa para validar permisos sobre la pantalla principal de FacturaPOS. */
export const PROGRAMA_FACTURA_POS = 'facturapos';
const TIPO_DOCUMENTO_MOTOR_VENTAS_CONTADO = 'Ventas Contado';

const moduloContabilidadInstalado = (modulos) => {
  if (modulos == null || modulos === false || modulos === 'false') return false;
  const partes = Array.isArray(modulos) ? modulos : String(modulos).split(/[,;]/);
  return partes.some((m) => String(m || '').trim().toLowerCase() === 'contabilidad');
};

const enviarMotorContableActivo = (valor) => String(valor || '').trim().toUpperCase() === 'S';

const debeEnviarAlMotor = (modulos, enviarMotorContable) =>
  moduloContabilidadInstalado(modulos) && enviarMotorContableActivo(enviarMotorContable);

const bloqueMotorDeTipo = (tipooperacion) => {
  const clave = String(tipooperacion || '').toLowerCase().replace(/\s/g, '');
  if (!clave) return null;
  return tiposDocumentosJson.find((e) => e?.[clave])?.[clave] || null;
};

const origenFormaPagoReglas = (tipooperacion) => {
  const bloque = bloqueMotorDeTipo(tipooperacion);
  if (!bloque) return [];
  const raw = bloque.origenformapago ?? bloque.origenformadepago ?? [];
  return (Array.isArray(raw) ? raw : [raw]).map((x) => String(x || '').trim()).filter(Boolean);
};

const tipoTieneOrigenFormaPago = (tipooperacion) => origenFormaPagoReglas(tipooperacion).length > 0;

const payloadFormaPagoSiAplica = (tipoDocumento, documentoFormaPago) => {
  const tipo = String(tipoDocumento || '').trim();
  const documento = String(documentoFormaPago || '').trim();
  if (!tipo || !tipoTieneOrigenFormaPago(tipo) || !documento) return {};
  return { forma_pago: documento };
};

const codigoUsaOrigenFormaPago = (tipooperacion, codigoRegla) => {
  const codigo = String(codigoRegla || '').trim().toLowerCase();
  if (!codigo) return false;
  return origenFormaPagoReglas(tipooperacion).some((c) => c.toLowerCase() === codigo);
};

const documentoMotorDefinido = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  if (!tipo) return false;
  return (registros || []).some((r) => {
    if (String(r?.tipo_documento || '').trim() !== tipo) return false;
    if (codigoUsaOrigenFormaPago(tipo, r?.codigo_regla)) return true;
    const aplica = String(r?.aplica_a || 'cuenta').trim().toLowerCase();
    if (aplica === 'json') return true;
    return Boolean(String(r?.cuentacontable || '').trim());
  });
};

const reglasMotorDeDocumento = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  return (registros || []).filter((r) => String(r?.tipo_documento || '').trim() === tipo);
};

const CLAVES_META_MOTOR = new Set([
  'tipodocumento',
  'nfactura',
  'documento',
  'numero',
  'suplidor',
  'fecha',
  'tasa',
  'usuario',
  'concepto',
  'valor',
  'valor_total',
  'lineas',
  'cuentas',
]);

const redondearDinero2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const construirDocumentoMotorVentasContado = (reglas, ctx) => {
  const tipoDoc = TIPO_DOCUMENTO_MOTOR_VENTAS_CONTADO;
  const lineasFp = Array.isArray(ctx.lineas) ? ctx.lineas.filter((ln) => ln?.cuenta && (ln.monto || 0) > 0) : [];
  const valor = redondearDinero2(ctx.valorTotal);
  const montos = ctx.montos || {};
  const doc = {
    tipodocumento: tipoDoc,
    nfactura: ctx.nfactura,
    documento: ctx.documento,
    numero: ctx.numero,
    fecha: ctx.fecha,
    tasa: ctx.tasa,
    usuario: ctx.usuario,
    concepto: ctx.concepto,
    valor,
    lineas: lineasFp,
    permitir_descuadre: true,
  };
  (reglas || []).forEach((regla) => {
    const codigo = String(regla?.codigo_regla || '').trim();
    if (!codigo || CLAVES_META_MOTOR.has(codigo.toLowerCase())) return;
    const usaFp =
      codigoUsaOrigenFormaPago(tipoDoc, codigo) ||
      String(regla?.aplica_a || '').trim().toLowerCase() === 'json';
    if (usaFp) {
      doc[codigo] = lineasFp;
      return;
    }
    const extra = montos[codigo] ?? montos[codigo.toLowerCase()];
    doc[codigo] = redondearDinero2(extra != null ? extra : valor);
  });
  return doc;
};

const pagosMetodoParaMotor = (datosPago) =>
  Object.entries(datosPago?.pagos || {}).filter(
    ([clave, pago]) => clave !== 'nota_credito' && (parseFloat(pago?.monto) || 0) > 0
  );

const resolverFilaFormaPago = (pago, formasPagoDb) => {
  const doc = String(pago?.documento || pago?.metodo || '').trim();
  if (!doc) return null;
  const norm = doc.toLowerCase();
  return (
    (formasPagoDb || []).find((r) => String(r.documento || '').trim().toLowerCase() === norm) ||
    (formasPagoDb || []).find((r) => String(r.documento || '').trim().toLowerCase().includes(norm)) ||
    (formasPagoDb || []).find((r) => norm.includes(String(r.documento || '').trim().toLowerCase())) ||
    null
  );
};

const documentoFormaPagoDesdePago = (datosPago, formasPagoDb) => {
  const pagos = pagosMetodoParaMotor(datosPago);
  const primero = pagos[0]?.[1];
  const fila = resolverFilaFormaPago(primero, formasPagoDb);
  return String(
    primero?.documento || fila?.documento || datosPago?.metodoPrincipal || ''
  ).trim();
};

const lineasMotorDesdePago = (datosPago, formasPagoDb) =>
  pagosMetodoParaMotor(datosPago)
    .map(([, pago]) => {
      const fila = resolverFilaFormaPago(pago, formasPagoDb);
      const cuenta = String(pago?.cuenta_contable || fila?.cuenta_contable || '').trim();
      const monto = redondearDinero2(pago?.monto);
      return cuenta && monto > 0 ? { cuenta, monto } : null;
    })
    .filter(Boolean);

/** Códigos e-CF permitidos en POS (sufijo del nombre, p.ej. "CONSUMO:E32" → 32). */
const CODIGOS_DOCUMENTO_POS = ['31', '32', '44', '45','99'];

/** Extrae el código numérico del tipo fiscal (campo codigo o sufijo tras ":" en nombre). */
const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || '').replace(/\D/g, '');
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || '').split(':')[1]?.replace(/\D/g, '') || '';
};

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const cellHeadSx = {
  bgcolor: FP.color.surfaceAlt,
  color: FP.color.textSecondary,
  fontSize: '0.74rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.4,
};

const itemHeadLabelSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const itemsTableSx = {
  tableLayout: 'fixed',
  width: '100%',
  minWidth: { xs: 720, sm: 820 },
};

const itemsAnchosColumnas = ['11%', '29%', '15%', '12%', '14%', '12%', '7%'];
const itemsColgroup = (
  <colgroup>
    {itemsAnchosColumnas.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
);

const FacturaPOS = () => {
  const { controller, selectedCompany, showSuccess, showError, showWarning } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles, modulos, comprobanteelectronico, nombreNegocio, rnc_empresa, direccion_empresa, enviarMotorContable } =
    controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const sucursalActual = useMemo(() => localStorage.getItem('sucursal') || '', []);

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_FACTURA_POS),
    [roleDetalles]
  );
  
  const puedeModificar = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_FACTURA_POS),
    [roleDetalles]
  );
  

  const [productos, setProductos] = useState([]);
  // 'detalle' | 'mayor': con 'mayor' el backend devuelve el campo `mayor` del
  // producto como precio (si lo tiene). Va en un ref para que cambiarlo no
  // dispare la recarga completa de cargarDatos.
  const [tipoPrecio, setTipoPrecio] = useState('detalle');
  const tipoPrecioRef = useRef('detalle');
  const [reglasImpuestos, setReglasImpuestos] = useState([]);
  const [reglasDescuentos, setReglasDescuentos] = useState([]);
  const [reglasDescuentosGenerales, setReglasDescuentosGenerales] = useState([]);
  const [tiposDocumentosFiscales, setTiposDocumentosFiscales] = useState([]);
  const [itemsFactura, setItemsFactura] = useState([]);
  const [impuestosFactura, setImpuestosFactura] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [calculadoraAbierta, setCalculadoraAbierta] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [valorCalculadora, setValorCalculadora] = useState('');

  const [tipoDocumento, setTipoDocumento] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [comprobantefiscal, setComprobantefiscal] = useState('');
  const [rnc, setRnc] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [opcionesRnc, setOpcionesRnc] = useState([]);
  const [cargandoRnc, setCargandoRnc] = useState(false);
  const busquedaRncTimerRef = useRef(null);

  const [dialogoPagoAbierto, setDialogoPagoAbierto] = useState(false);
  const [guardandoFactura, setGuardandoFactura] = useState(false);
  const guardandoFacturaRef = useRef(false);
  const [vistaFacturaAbierta, setVistaFacturaAbierta] = useState(false);
  const [datosVistaFactura, setDatosVistaFactura] = useState({});

  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [dialogoConsultaProducto, setDialogoConsultaProducto] = useState(false);

  const [dialogoCotizaciones, setDialogoCotizaciones] = useState(false);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [cargandoCotizaciones, setCargandoCotizaciones] = useState(false);
  const [busquedaCotizacion, setBusquedaCotizacion] = useState('');
  const [cotizacionSeleccionada, setCotizacionSeleccionada] = useState(null);
  const [formasPagoDb, setFormasPagoDb] = useState([]);

  const totalDescuentosLinea = itemsFactura.reduce(
    (sum, item) => sum + (parseFloat(item.descuento) || 0),
    0
  );
  const subtotalAntesDescuentos = itemsFactura.reduce(
    (sum, item) => sum + (parseFloat(item.cantidad) * parseFloat(item.precio) || 0),
    0
  );
  const subtotal = itemsFactura.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);

  const docFiscalSeleccionado = useMemo(
    () => tiposDocumentosFiscales.find((t) => t.nombre === tipoDocumento) || null,
    [tiposDocumentosFiscales, tipoDocumento]
  );

  // Documento tipo E32 (Factura de Consumo Electrónica): el RNC y la razón social
  // del cliente son opcionales; para cualquier otro comprobante son obligatorios.
  const esE32 = useMemo(() => {
    if (!docFiscalSeleccionado) return false;
    const encabezado = (docFiscalSeleccionado.nombre || '').split(':')[1]?.trim().toUpperCase() || '';
    return encabezado.startsWith('E32') || String(docFiscalSeleccionado.codigo) === '32';
  }, [docFiscalSeleccionado]);

  // E44 (Régimen especial): el XSD no discrimina ITBIS; no se calcula ni se graba.
  const esEspecial = useMemo(() => {
    if (!docFiscalSeleccionado) {
      return String(tipoDocumento || '').toLowerCase().includes('especial');
    }
    const codigo = codigoTipoDocumento(docFiscalSeleccionado);
    const encabezado = (docFiscalSeleccionado.nombre || '').split(':')[1]?.trim().toUpperCase() || '';
    return (
      codigo === '44' ||
      encabezado.startsWith('E44') ||
      String(tipoDocumento || '').toLowerCase().includes('especial')
    );
  }, [docFiscalSeleccionado, tipoDocumento]);

  const totalImpuestos = esEspecial
    ? 0
    : impuestosFactura.reduce((sum, imp) => sum + (parseFloat(imp.monto) || 0), 0);

  const reglaDescuentoGeneralAplicada = useMemo(() => {
    // Las reglas generales se guardan por documento de aplicación ("Factura POS"),
    // no por el e-CF fiscal seleccionado en el POS (p.ej. E31/E32).
    const reglasDoc = (reglasDescuentosGenerales || []).filter(
      (r) => r.activa === 'S' && r.tipo_documento === 'Factura POS'
    );
    const aplicables = reglasDoc.filter(
      (r) =>
        r.aplicar_solo_por_encima_monto !== 'S' ||
        (r.monto_minimo != null && subtotal >= parseFloat(r.monto_minimo))
    );
    if (aplicables.length === 0) return null;
    aplicables.sort(
      (a, b) => (parseFloat(b.monto_minimo) || 0) - (parseFloat(a.monto_minimo) || 0)
    );
    return aplicables[0];
  }, [reglasDescuentosGenerales, subtotal]);

  const descuentoGeneralTotal = useMemo(() => {
    if (!reglaDescuentoGeneralAplicada || subtotal <= 0) return 0;
    const pct = parseFloat(reglaDescuentoGeneralAplicada.porcentaje) || 0;
    return subtotal * (pct / 100);
  }, [reglaDescuentoGeneralAplicada, subtotal]);

  const totalDescuentos = totalDescuentosLinea + descuentoGeneralTotal;
  const total = subtotal - descuentoGeneralTotal + totalImpuestos;

  // La DGII exige identificar al comprador (RNC o cédula) en facturas de
  // consumo (E32) de RD$250,000 o más; por debajo el consumo va anónimo.
  const LIMITE_CONSUMO_SIN_RNC = 250000;
  const e32RequiereRnc = esE32 && total >= LIMITE_CONSUMO_SIN_RNC;
  // El RNC del comprador viaja en la factura salvo consumo pequeño anónimo.
  const rncComprador = esE32 && !e32RequiereRnc ? '' : rnc.trim();
  const razonSocialComprador = esE32 && !e32RequiereRnc ? '' : razonSocial.trim();

  const getDescuentoGeneralPorItem = (item) => {
    if (descuentoGeneralTotal <= 0 || subtotal <= 0) return 0;
    const itemSub = parseFloat(item.subtotal) || 0;
    return (itemSub / subtotal) * descuentoGeneralTotal;
  };

  const cargarProductos = useCallback(async (tipo = tipoPrecioRef.current) => {
    if (!usuarioLog) return null;
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerproductos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          anomesfacturacion: "productosnoserializados",
          localidad: localStorage.getItem('sucursal'),
          tipo_precio: tipo,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const lista = data.productos || [];
        setProductos(lista);
        return lista;
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar productos');
      }
    } catch {
      showError('Error de conexión al cargar productos');
    }
    return null;
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  const cargarReglasImpuestos = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerreglasimpuestos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setReglasImpuestos(data.reglas || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar reglas de impuestos');
      }
    } catch {
      showError('Error de conexión al cargar reglas de impuestos');
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  const cargarReglasDescuentos = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerreglasdescuentos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          // Misma tabla que ReglasDescuentos / obtenerproductos del POS.
          tabla_inventario: 'productosnoserializados',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setReglasDescuentos(data.reglas || []);
      } else {
        setReglasDescuentos([]);
      }
    } catch (error) {
      console.error('Error de conexión al cargar reglas de descuento:', error);
      setReglasDescuentos([]);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor]);

  const cargarReglasDescuentosGenerales = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerreglasdescuentosgenerales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setReglasDescuentosGenerales(data.reglas || []);
      }
    } catch (error) {
      console.error('Error al cargar reglas de descuentos generales:', error);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor]);

  const buscarRncs = useCallback(
    async (busqueda = '') => {
      if (!usuarioLog) return;
      setCargandoRnc(true);
      try {
        const response = await fetch(servidor + '/api/facturabackend_buscarrnc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            busqueda: (busqueda || '').trim(),
          }),
        });
        if (response.ok) {
          const data = await response.json();
          setOpcionesRnc(data.rncs || []);
        }
      } catch {
        // Silencioso: el campo sigue usable como texto libre.
      } finally {
        setCargandoRnc(false);
      }
    },
    [selectedCompany, usuarioLog, claveLog, servidor]
  );

  const cargarTiposDocumentosFiscales = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_listartiposdocumentos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        // Solo los documentos de CODIGOS_DOCUMENTO_POS se emiten desde el POS.
        // En particular quedan fuera las notas de crédito/débito (E33/E34):
        // requieren referencia a la factura de origen y tienen sus propias
        // pantallas; emitirlas como venta produce un e-CF inválido para la DGII.
        const tiposActivos = (data.tipos || []).filter(
          (tipo) =>
            tipo.activo === 'S' &&
            CODIGOS_DOCUMENTO_POS.includes(codigoTipoDocumento(tipo))
        );
        setTiposDocumentosFiscales(tiposActivos);
        if (tiposActivos.length > 0) {
          setTipoDocumento((prev) => prev || tiposActivos[1].nombre);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar tipos de documentos fiscales');
      }
    } catch {
      showError('Error de conexión al cargar tipos de documentos fiscales');
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      await Promise.all([
        cargarProductos(),
        cargarReglasImpuestos(),
        cargarReglasDescuentos(),
        cargarReglasDescuentosGenerales(),
        cargarTiposDocumentosFiscales(),
      ]);
    } catch {
      showError('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  }, [
    cargarProductos,
    cargarReglasImpuestos,
    cargarReglasDescuentos,
    cargarReglasDescuentosGenerales,
    cargarTiposDocumentosFiscales,
    showError,
  ]);

  const cargarFormasPago = useCallback(async () => {
    if (!usuarioLog || !selectedCompany?.db_empresa || !moduloContabilidadInstalado(modulos)) {
      setFormasPagoDb([]);
      return;
    }
    try {
      const response = await fetch(`${servidor}/configuracionbackend_listarformasdepago`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      setFormasPagoDb(response.ok ? data.formas_pago || [] : []);
    } catch {
      setFormasPagoDb([]);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, modulos]);

  const enviarAlMotorContableVentasContado = useCallback(
    async (ctxDocumento) => {
      if (!debeEnviarAlMotor(modulos, enviarMotorContable)) return;
      if (!selectedCompany?.db_empresa) return;
      try {
        const tipoDoc = TIPO_DOCUMENTO_MOTOR_VENTAS_CONTADO;
        const reglasRes = await fetch(`${servidor}/contabilidadbackend_listarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tipodocumento: tipoDoc,
            ...payloadFormaPagoSiAplica(tipoDoc, ctxDocumento.formaPago),
          }),
        });
        const reglasData = await reglasRes.json().catch(() => ({}));
        if (!reglasRes.ok) {
          showWarning(reglasData.Error || 'No se pudieron consultar las reglas del motor contable');
          return;
        }
        const reglasDoc = reglasMotorDeDocumento(reglasData.registros, tipoDoc);
        if (!documentoMotorDefinido(reglasDoc, tipoDoc)) {
          showWarning(
            'No hay reglas del motor para Ventas Contado; no se registró en diario/histdia'
          );
          return;
        }
        const payloadDocumento = construirDocumentoMotorVentasContado(reglasDoc, ctxDocumento);
        const motorRes = await fetch(`${servidor}/contabilidadbackend_enviarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            modulos,
            documento: payloadDocumento,
          }),
        });
        const motorData = await motorRes.json().catch(() => ({}));
        if (!motorRes.ok) {
          showWarning(motorData.Error || 'No se pudo enviar la venta al motor contable');
          return;
        }
        if (motorData.omitido) return;
        if (motorData.ok) {
          if (motorData.descuadrado) {
            showWarning(motorData.mensaje || 'Motor contable: asiento guardado descuadrado');
          } else {
            showSuccess(motorData.mensaje || 'Motor contable: asiento generado');
          }
        } else if (motorData.mensaje) {
          showWarning(motorData.mensaje);
        }
      } catch {
        showWarning('Error de conexión al enviar la venta al motor contable');
      }
    },
    [modulos, enviarMotorContable, servidor, selectedCompany, usuarioLog, claveLog, showWarning, showSuccess]
  );

  useEffect(() => {
    if (puedeLeer) cargarDatos();
  }, [puedeLeer, cargarDatos]);

  useEffect(() => {
    if (puedeLeer) cargarFormasPago();
  }, [puedeLeer, cargarFormasPago]);

  useEffect(() => {
    if (puedeLeer && (!esE32 || e32RequiereRnc)) {
      buscarRncs('');
    }
  }, [puedeLeer, esE32, e32RequiereRnc, buscarRncs]);

  useEffect(
    () => () => {
      if (busquedaRncTimerRef.current) {
        clearTimeout(busquedaRncTimerRef.current);
      }
    },
    []
  );

  const esEspecialRef = useRef(esEspecial);
  esEspecialRef.current = esEspecial;

  const cambiarDocumentoFiscal = (nuevoTipo) => {
    if (nuevoTipo === tipoDocumento) return;
    setTipoDocumento(nuevoTipo);
    setItemsFactura([]);
    setImpuestosFactura([]);
  };

  const calcularImpuestos = useCallback(async () => {
    if (esEspecialRef.current || subtotal <= 0) {
      setImpuestosFactura([]);
      return;
    }
    try {
      const response = await fetch(servidor + '/api/facturabackend_calcularimpuestos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          subtotal,
          itemsfactura: itemsFactura,
        }),
      });
      if (esEspecialRef.current) {
        setImpuestosFactura([]);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        if (data.impuestos && Array.isArray(data.impuestos)) {
          setImpuestosFactura(
            data.impuestos.map((imp) => ({
              id: imp.id || imp.codigo || Date.now() + Math.random(),
              codigo: imp.codigo || imp.nombre || 'IMP',
              nombre: imp.nombre || imp.descripcion || 'Impuesto',
              porcentaje: parseFloat(imp.porcentaje) || 0,
              monto: parseFloat(imp.monto) || 0,
            }))
          );
        } else {
          setImpuestosFactura([]);
        }
      }
    } catch (error) {
      console.error('Error al calcular impuestos:', error);
      setImpuestosFactura([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esEspecial, subtotal, reglasImpuestos, selectedCompany, usuarioLog, claveLog, servidor]);

  useEffect(() => {
    calcularImpuestos();
  }, [calcularImpuestos]);

  const aplicarReglasDescuento = (producto, cantidad = 1) => {
    let descuentoTotal = parseFloat(producto.descuento) || 0;
    const precioUnitario = parseFloat(producto.precio) || 0;
    let cantidadConDescuento = cantidad;

    const reglasAplicables = reglasDescuentos.filter((regla) => {
      if (regla.tipoAplicacion === 'producto') {
        return reglaDescuentoAplicaAProducto(regla, producto);
      }
      if (
        regla.tipoAplicacion === 'departamento' &&
        producto.departamento &&
        String(regla.departamento || '').trim() === String(producto.departamento || '').trim()
      ) {
        return true;
      }
      return false;
    });

    if (reglasAplicables.length > 0) {
      const regla = reglasAplicables[0];
      if (regla.tipoDescuento === 'porcentaje') {
        const porcentajeDescuento = parseFloat(regla.porcentaje) || 0;
        const descuentoPorUnidad = precioUnitario * (porcentajeDescuento / 100);
        descuentoTotal = descuentoPorUnidad * cantidad;
      } else if (regla.tipoDescuento === '2x1') {
        const pares = Math.floor(cantidad / 2);
        const unidadesGratis = pares;
        cantidadConDescuento = cantidad - unidadesGratis;
        descuentoTotal = unidadesGratis * precioUnitario;
      }
    }

    return { descuento: descuentoTotal, cantidad: cantidadConDescuento, precioUnitario };
  };

  const agregarProducto = (producto) => {
    if (!puedeModificar) {
      showError('No tiene permisos para registrar facturas.');
      return;
    }
    const itemExistente = itemsFactura.find(
      (item) => item.codigo === producto.codigo && item.tipo === 'producto'
    );
    const descuentoInfo = aplicarReglasDescuento(producto, 1);
    const descuentoProducto = descuentoInfo.descuento;

    if (itemExistente) {
      const nuevaCantidad = parseFloat(itemExistente.cantidad) + 1;
      const descuentoInfoActualizado = aplicarReglasDescuento(producto, nuevaCantidad);
      const nuevoDescuento = descuentoInfoActualizado.descuento;
      setItemsFactura(
        itemsFactura.map((item) => {
          if (item.id === itemExistente.id) {
            const subtotalItem = nuevaCantidad * parseFloat(item.precio) - nuevoDescuento;
            return {
              ...item,
              cantidad: nuevaCantidad,
              descuento: nuevoDescuento,
              subtotal: Math.max(0, subtotalItem),
            };
          }
          return item;
        })
      );
    } else {
      const subtotalItem = 1 * parseFloat(producto.precio) - descuentoProducto;
      const nuevoItem = {
        id: Date.now(),
        codigo: producto.codigo,
        descripcion: producto.descripcion,
        tipo: 'producto',
        cantidad: 1,
        precio: parseFloat(producto.precio) || 0,
        descuento: descuentoProducto,
        descuento_general: 0,
        subtotal: Math.max(0, subtotalItem),
        existencia: parseFloat(producto.existencia) || 0,
        departamento: producto.departamento || null,
        impuesto: esEspecial ? 0 : producto.impuesto || null,
        nombreimpuesto: esEspecial ? null : producto.nombreimpuesto || null,
        tipoproducto: 'item',
        localidad: producto.localidad || null,
      };
      setItemsFactura([...itemsFactura, nuevoItem]);
    }
    setBusquedaProducto('');
  };

  const eliminarItem = (id) => setItemsFactura(itemsFactura.filter((item) => item.id !== id));

  const actualizarCantidadItem = (id, nuevaCantidad) => {
    if (nuevaCantidad <= 0) {
      eliminarItem(id);
      return;
    }
    setItemsFactura(
      itemsFactura.map((item) => {
        if (item.id === id) {
          let nuevoDescuento = 0;
          if (item.tipo === 'producto') {
            const productoOriginal = productos.find(
              (p) => String(p.codigo ?? '').trim() === String(item.codigo ?? '').trim()
            );
            if (productoOriginal) {
              const descuentoInfo = aplicarReglasDescuento(productoOriginal, nuevaCantidad);
              nuevoDescuento = descuentoInfo.descuento;
            } else {
              const descuentoPorUnidad =
                parseFloat(item.cantidad) > 0
                  ? parseFloat(item.descuento || 0) / parseFloat(item.cantidad)
                  : 0;
              nuevoDescuento = nuevaCantidad * descuentoPorUnidad;
            }
          } else {
            const descuentoPorUnidad =
              parseFloat(item.cantidad) > 0
                ? parseFloat(item.descuento || 0) / parseFloat(item.cantidad)
                : 0;
            nuevoDescuento = nuevaCantidad * descuentoPorUnidad;
          }
          const subtotalItem = nuevaCantidad * parseFloat(item.precio) - nuevoDescuento;
          return {
            ...item,
            cantidad: nuevaCantidad,
            descuento: nuevoDescuento,
            subtotal: Math.max(0, subtotalItem),
          };
        }
        return item;
      })
    );
  };

  const abrirCalculadora = (item) => {
    setItemEditando(item);
    setValorCalculadora(item.cantidad.toString());
    setCalculadoraAbierta(true);
  };

  const cerrarCalculadora = () => {
    setCalculadoraAbierta(false);
    setItemEditando(null);
    setValorCalculadora('');
  };

  const manejarTeclaCalculadora = (valor) => {
    if (valor === 'C') setValorCalculadora('');
    else if (valor === '=') {
      const cantidad = parseFloat(valorCalculadora) || 0;
      if (itemEditando) actualizarCantidadItem(itemEditando.id, cantidad);
      cerrarCalculadora();
    } else if (valor === '←') setValorCalculadora((prev) => prev.slice(0, -1));
    else setValorCalculadora((prev) => prev + valor);
  };

  // Cambia entre precio de detalle y x mayor: recarga los productos con el
  // tipo nuevo y reprecia las líneas que ya están en la factura (los
  // descuentos por regla se recalculan sobre el precio nuevo).
  const cambiarTipoPrecio = async (_e, nuevo) => {
    if (!nuevo || nuevo === tipoPrecio) return;
    tipoPrecioRef.current = nuevo;
    setTipoPrecio(nuevo);
    setCargando(true);
    const lista = await cargarProductos(nuevo);
    setCargando(false);
    if (!lista) return;
    setItemsFactura((items) =>
      items.map((item) => {
        if (item.tipo !== 'producto') return item;
        const prod = lista.find((p) => p.codigo === item.codigo);
        if (!prod) return item;
        const precio = parseFloat(prod.precio) || 0;
        const cantidad = parseFloat(item.cantidad) || 0;
        const descuento = aplicarReglasDescuento(prod, cantidad).descuento;
        return {
          ...item,
          precio,
          descuento,
          subtotal: Math.max(0, cantidad * precio - descuento),
        };
      })
    );
  };

  const limpiarFactura = () => {
      setItemsFactura([]);
      setImpuestosFactura([]);
      if (
        tiposDocumentosFiscales.length > 0 &&
        !tiposDocumentosFiscales.find((t) => t.codigo === tipoDocumento)
      ) {
        setTipoDocumento(tiposDocumentosFiscales[1].nombre);
      }
      setObservaciones('');
      setRnc('');
      setRazonSocial('');
    
  };

  const abrirVistaFactura = (datosGuardados = null) => {
    const itemsConDescuentoGeneral = itemsFactura.map((item) => ({
      ...item,
      subtotal: Math.max(0, (parseFloat(item.subtotal) || 0) - getDescuentoGeneralPorItem(item)),
      descuento_general:
        (parseFloat(item.descuento_general) || 0) + getDescuentoGeneralPorItem(item),
    }));
    const ncfFactura = datosGuardados?.comprobantefiscal ?? comprobantefiscal;
    setDatosVistaFactura({
      items: itemsConDescuentoGeneral,
      impuestos: esEspecial ? [] : impuestosFactura,
      subtotal: subtotal - descuentoGeneralTotal,
      totalDescuentos,
      totalImpuestos: esEspecial ? 0 : totalImpuestos,
      total,
      documento: datosGuardados?.documento ?? tipoDocumento,
      numero: datosGuardados?.numero ?? '',
      fecha: hoyLocal(),
      ncf: ncfFactura,
      tipoDocumento:
        docFiscalSeleccionado?.nombre || tipoDocumento || 'FACTURA POS',
      observaciones,
      empresa: selectedCompany?.empresa || selectedCompany?.nombre || selectedCompany,
      id_empresa: selectedCompany,
      // Datos del comprobante fiscal electronico: el QR y la fecha de validez del
      // NCF solo se despliegan en la impresion cuando comprobanteelectronico === 'S'.
      comprobanteelectronico,
      codigocomprobante: docFiscalSeleccionado?.codigo ?? '',
      fechavencimiento: docFiscalSeleccionado?.fecha_valida ?? docFiscalSeleccionado?.valido ?? '',
      // RNC del comprador para el QR y la vista (en E32 solo si el monto lo exige).
      rnc: rncComprador,
      cliente_nombre: razonSocialComprador,
      fecha_firma: datosGuardados?.fecha_firma ?? '',
      codigo_seguridad: datosGuardados?.codigo_seguridad ?? '',
      ...(datosGuardados && { totalNotaCredito: datosGuardados.totalNotaCredito }),
    });
    setVistaFacturaAbierta(true);
  };

  // Descarga en el navegador el XML firmado del e-CF (texto → blob).
  const descargarXmlECF = (xmlTexto, filename) => {
    descargarArchivoECF(new Blob([xmlTexto], { type: 'text/xml' }), filename);
  };

  // Descarga en el navegador el Excel del e-CF devuelto por el endpoint de la DGII.
  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'factura_ecf.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const guardarFactura = async (datosPago) => {
    if (guardandoFacturaRef.current) return;

    guardandoFacturaRef.current = true;
    setGuardandoFactura(true);

    try {
    if (!puedeModificar) {
      showError('No tiene permisos para guardar facturas.');
      return;
    }

    const itemsPayload = itemsFactura.map((item) => {
      const descGeneralItem = getDescuentoGeneralPorItem(item);
      return {
        ...item,
        descuento: parseFloat(item.descuento) || 0,
        descuento_general: descGeneralItem,
        subtotal: Math.max(0, (parseFloat(item.subtotal) || 0) - descGeneralItem),
        impuesto: esEspecial ? 0 : parseFloat(item.impuesto) || 0,
      };
    });

    const fechaMotor = hoyLocal();
    const enviarMotor = debeEnviarAlMotor(modulos, enviarMotorContable);
    const tipoDocMotor = TIPO_DOCUMENTO_MOTOR_VENTAS_CONTADO;
    const documentoFormaPago = enviarMotor
      ? documentoFormaPagoDesdePago(datosPago, formasPagoDb)
      : '';
    const lineasMotor = enviarMotor ? lineasMotorDesdePago(datosPago, formasPagoDb) : [];
    const montosMotor = enviarMotor
      ? {
          ingresos: redondearDinero2(subtotalAntesDescuentos),
          itbis: redondearDinero2(esEspecial ? 0 : totalImpuestos),
          descuentos: redondearDinero2(totalDescuentos),
          cajageneral: redondearDinero2(total),
        }
      : null;
    const payloadMotor = enviarMotor
      ? {
          modulos,
          tipodocumento: tipoDocMotor,
          ...payloadFormaPagoSiAplica(tipoDocMotor, documentoFormaPago),
          lineas_motor: lineasMotor,
          montos: montosMotor,
        }
      : {};

    const body = {
      id_empresa: selectedCompany,
      usuario: usuarioLog || '',
      password: claveLog || '',
      anomesfacturacion: "productosnoserializados",
      localidad: localStorage.getItem('sucursal') || null,
      ...payloadMotor,
      factura: {
        cliente: rncComprador || '999999',
        razonsocial: razonSocialComprador,
        items: itemsPayload,
        impuestos: esEspecial ? [] : impuestosFactura,
        subtotal: subtotal - descuentoGeneralTotal,
        total_descuentos: totalDescuentos,
        total_impuestos: esEspecial ? 0 : totalImpuestos,
        total,
        ncf: '',
        tipo_documento: tipoDocumento,
        tipo_factura: 'FACTURA POS',
        fecha: fechaMotor,
        usuario: usuarioLog,
        observaciones,
        pago: datosPago || null,
        cotizacion_id: cotizacionSeleccionada || null,
        modulosasignados: modulos,
        comprobanteelectronico: comprobanteelectronico === 'S' ? 'S' : 'N',
        ...(enviarMotor
          ? {
              tipodocumento: tipoDocMotor,
              ...payloadFormaPagoSiAplica(tipoDocMotor, documentoFormaPago),
            }
          : {}),
      },
    };

    const { ok, data, error } = await guardarFacturaBackend(servidor, body);
    if (!ok) {
      showError(error || 'Error al guardar la factura');
      return;
    }

    showSuccess(`Factura ${data.documento}-${data.numero} guardada exitosamente`);
    setComprobantefiscal(data.comprobantefiscal);
    // Refresca el catálogo RNC por si el backend acaba de registrar uno nuevo.
    buscarRncs('');

    let fechaFirmaEcf = '';
    let codigoSeguridadEcf = '';
    let montoTotalEcf = null;

    // Si la empresa emite comprobante fiscal electrónico, se reenvía el mismo
    // detalle de la factura al endpoint del e-CF (DGII) para generar el Excel.
    if (comprobanteelectronico === 'S') {
      const ecfPayload = {
        comprobanteelectronico,
        rnc: rncComprador,
        razonsocial: razonSocialComprador,
        comprobante: data.comprobantefiscal || '',
        tipocomprobante: tipoDocumento,
        usuario: usuarioLog || '',
        password: claveLog || '',
        pago: datosPago || null,
        items: itemsPayload.map((it) => ({
          item: it.descripcion,
          cantidad: parseFloat(it.cantidad) || 0,
          valor: parseFloat(it.precio) || 0,
          descuento: (parseFloat(it.descuento) || 0) + (parseFloat(it.descuento_general) || 0),
          tasaitbis: esEspecial ? 0 : parseFloat(it.impuesto) || 0,
        })),
      };
      const ecf = await enviarPayloadFacturaElectronica(servidor, ecfPayload);
      if (ecf.ok) {
        descargarArchivoECF(ecf.blob, ecf.filename);
      } else {
        showError(ecf.error || 'No se pudo generar el comprobante electrónico');
      }

      // Fase 3 e-CF: firma, registra en ecf_emitidos y envía a la DGII
      // (consumo <250k va como RFCE). Si la DGII está caída el comprobante
      // queda pendiente de reenvío; si falta el certificado se avisa sin
      // bloquear la venta (el Excel ya salió).
      const emision = await emitirEcfDgii(servidor, {
        ...ecfPayload,
        rncemisor: rnc_empresa || '',
        razonsocialemisor: typeof nombreNegocio === 'string' ? nombreNegocio : '',
        direccionemisor: direccion_empresa || '',
      });
      if (emision.ok) {
        descargarXmlECF(emision.xml, `${emision.encf || 'ecf'}.xml`);
        // Consumo <250k: también se descarga el resumen RFCE firmado, que es
        // el archivo que realmente viaja a la DGII (fc.dgii.gov.do).
        if (emision.xml_rfce) {
          descargarXmlECF(emision.xml_rfce, `${emision.encf || 'ecf'}-resumen.xml`);
        }
        fechaFirmaEcf = emision.fecha_firma || '';
        codigoSeguridadEcf = emision.codigo_seguridad || '';
        montoTotalEcf = emision.monto_total ?? null;
        const etiquetas = {
          aceptado: 'aceptado por la DGII',
          aceptado_condicional: 'aceptado condicional por la DGII',
          en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ''}`,
          rechazado: 'RECHAZADO por la DGII',
          pendiente_envio: 'firmado; DGII inaccesible, se reenviará',
          error: 'firmado; el envío a la DGII falló',
        };
        // Mensajes literales que devuelve la DGII (motivos de rechazo o
        // advertencias del aceptado condicional), p. ej.
        // "Fecha de vencimiento de secuencia inválida. (145)".
        const detalleDgii = (emision.respuesta_dgii?.mensajes || [])
          .map((m) => `${m.valor || m.mensaje || ''}${m.codigo ? ` (${m.codigo})` : ''}`)
          .filter((t) => t.trim())
          .join(' · ');
        const mensaje =
          `e-CF ${emision.encf} ${etiquetas[emision.estado] || emision.estado}` +
          ` · código ${emision.codigo_seguridad}` +
          (detalleDgii ? ` — DGII: ${detalleDgii}` : '');
        if (emision.estado === 'rechazado') {
          showError(mensaje);
          const revertBody = {
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            nfact: data.numero,
            tipo_documento: tipoDocumento,
            anomesfacturacion: 'productosnoserializados',
            motoraguardar: 'N',
            comprobante: data.comprobantefiscal || '',
            ncf: data.comprobantefiscal || '',
          };
          const rev = await revertirFacturaRechazada(servidor, revertBody);
          if (rev.ok) {
            showError(`Factura ${data.documento}-${data.numero} revertida automáticamente (e-CF rechazado)`);
          } else {
            showError(rev.error || 'No se pudo revertir la factura rechazada');
          }
          limpiarFactura();
          return;
        } else {
          showSuccess(mensaje);
        }
      } else {
        showError(emision.error || 'No se pudo emitir el e-CF');
      }
    }

    if (enviarMotor) {
      const ctxMotor = {
        valorTotal: redondearDinero2(total),
        formaPago: documentoFormaPago,
        lineas: lineasMotor,
        nfactura: String(data.numero || ''),
        documento: data.documento || tipoDocumento || 'FACTURA POS',
        numero: data.numero,
        fecha: fechaMotor,
        tasa: 1,
        usuario: usuarioLog || '',
        concepto: `Venta contado ${data.documento || tipoDocumento}-${data.numero}`,
        montos: montosMotor,
      };
      const motorSave = data.motor;
      if (motorSave?.registrado) {
        if (motorSave.descuadrado) {
          showWarning(motorSave.mensaje || 'Motor contable: asiento guardado descuadrado');
        } else {
          showSuccess(motorSave.mensaje || 'Motor contable: asiento registrado en diario e histdia');
        }
      } else if (motorSave?.omitido) {
        // Sin reglas: no se reenvía.
      } else if (motorSave) {
        if (motorSave.mensaje) showWarning(motorSave.mensaje);
      } else {
        await enviarAlMotorContableVentasContado(ctxMotor);
      }
    }

    abrirVistaFactura({
      documento: data.documento,
      numero: String(data.numero),
      comprobantefiscal: data.comprobantefiscal,
      totalNotaCredito: datosPago?.pagos?.nota_credito?.monto ?? 0,
      fecha_firma: fechaFirmaEcf,
      codigo_seguridad: codigoSeguridadEcf,
      monto_total_ecf: montoTotalEcf,
    });
    limpiarFactura();
    } finally {
      guardandoFacturaRef.current = false;
      setGuardandoFactura(false);
    }
  };

  const manejarGuardarFacturaClick = () => {
    if (guardandoFactura) return;
    if (!puedeModificar) {
      showError('No tiene permisos para registrar facturas.');
      return;
    }
    if (itemsFactura.length === 0) {
      showError('Debe agregar al menos un item a la factura');
      return;
    }
    if (!tipoDocumento) {
      showError('Debe seleccionar un tipo de documento fiscal');
      return;
    }
    // El RNC/Cédula y la razón social son obligatorios para todo comprobante
    // distinto de Consumo (E32), y también para E32 desde RD$250,000 (la DGII
    // exige identificar al comprador a partir de ese monto).
    if (!esE32 || e32RequiereRnc) {
      const motivo = e32RequiereRnc
        ? 'para facturas de consumo de RD$250,000 o más'
        : 'para este tipo de documento';
      if (!rnc.trim()) {
        showError(`Debe ingresar el RNC / Cédula del cliente ${motivo}`);
        return;
      }
      if (!razonSocial.trim()) {
        showError(`Debe ingresar la razón social / nombre del cliente ${motivo}`);
        return;
      }
    }
    setDialogoPagoAbierto(true);
  };

  const cargarCotizaciones = useCallback(
    async (busqueda = '') => {
      if (!usuarioLog) return;
      setCargandoCotizaciones(true);
      try {
        const response = await fetch(servidor + '/api/facturabackend_listarcotizaciones', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            busqueda,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          setCotizaciones(data.cotizaciones || []);
        } else {
          const errorData = await response.json().catch(() => ({}));
          showError(errorData.Error || 'Error al cargar cotizaciones');
        }
      } catch {
        showError('Error de conexión al cargar cotizaciones');
      } finally {
        setCargandoCotizaciones(false);
      }
    },
    [selectedCompany, usuarioLog, claveLog, servidor, showError]
  );

  const abrirDialogoCotizaciones = async () => {
    setDialogoCotizaciones(true);
    setBusquedaCotizacion('');
    await cargarCotizaciones('');
  };

  const importarCotizacion = async (cotizacionId) => {
    if (!puedeModificar) {
      showError('No tiene permisos para importar cotizaciones.');
      return;
    }
    try {
      setCotizacionSeleccionada(cotizacionId);
      const response = await fetch(servidor + '/api/facturabackend_obtenercotizacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          cotizacion_id: cotizacionId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const cotizacion = data.cotizacion;
        const items = data.items || [];
        if (items.length === 0) {
          showError('La cotización seleccionada no tiene items');
          return;
        }
        const nuevosItems = items.map((item, index) => ({
          id: Date.now() + index,
          codigo: item.codigo,
          descripcion: item.descripcion,
          cantidad: parseFloat(item.cantidad) || 1,
          precio: parseFloat(item.precio) || 0,
          descuento: parseFloat(item.descuento) || 0,
          subtotal: parseFloat(item.subtotal) || 0,
          tipo: item.tipo_item || 'producto',
        }));
        setItemsFactura(nuevosItems);
        if (cotizacion.observaciones) setObservaciones(cotizacion.observaciones);
        showSuccess(
          `Cotización "${cotizacion.numero} - ${cotizacion.nombre}" importada exitosamente`
        );
        setDialogoCotizaciones(false);
        setBusquedaCotizacion('');
        try {
          await fetch(servidor + '/api/facturabackend_marcarcotizacionimportada', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              cotizacion_id: cotizacionId,
            }),
          });
        } catch {
          /* ignore */
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al importar cotización');
      }
    } catch {
      showError('Error de conexión al importar cotización');
    }
  };

  const handleBuscarCotizacion = async (busqueda) => {
    setBusquedaCotizacion(busqueda);
    await cargarCotizaciones(busqueda);
  };

  const productosFiltrados = productos.filter((p) => {
    if (!busquedaProducto) return true;
    const busqueda = String(busquedaProducto || '').toLowerCase();
    const descripcion = String(p.descripcion || '').toLowerCase();
    const codigo = String(p.codigo || '').toLowerCase();
    return descripcion.includes(busqueda) || codigo.includes(busqueda);
  });

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader
          title="Punto de venta"
          subtitle="Factura POS — búsqueda de productos y registro rápido"
          cta={
            <Button
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => setDialogoConsultaProducto(true)}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
              }}
            >
              Consultar producto
            </Button>
          }
        />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <ReceiptIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para acceder al punto de venta.
            </Typography>
          </CardContent>
        </Card>
        <ConsultaProducto
          open={dialogoConsultaProducto}
          onClose={() => setDialogoConsultaProducto(false)}
        />
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Punto de venta"
        subtitle="Factura POS — búsqueda de productos y registro rápido"
        cta={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => setDialogoConsultaProducto(true)}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
              }}
            >
              Consultar producto
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={abrirDialogoCotizaciones}
              disabled={!puedeModificar || !usuarioLog}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
                '&.Mui-disabled': { color: FP.color.textMuted },
              }}
            >
              Importar cotización
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={2} sx={{ flex: 1, mt: 0.5 }}>
        <Grid item xs={12} md={5}>
          <Card sx={{ ...cardShellSx, display: 'flex', flexDirection: 'column', minHeight: '600px' }}>
            <CardContent
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 2.5,
                minHeight: 0,
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    mb: 2,
                    pb: 1.2,
                    borderBottom: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.2,
                      bgcolor: FP.color.accentBlueSoft,
                      color: FP.color.accentBlue,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Inventory2Icon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography
                    sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}
                  >
                    Productos
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={tipoPrecio}
                    onChange={cambiarTipoPrecio}
                    disabled={cargando}
                    sx={{
                      ml: 'auto !important',
                      '& .MuiToggleButton-root': {
                        px: 1.4,
                        py: 0.3,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        textTransform: 'none',
                      },
                      '& .Mui-selected': {
                        bgcolor: `${FP.color.accentBlueSoft} !important`,
                        color: `${FP.color.accentBlue} !important`,
                      },
                    }}
                  >
                    <ToggleButton value="detalle">Detalle</ToggleButton>
                    <ToggleButton value="mayor">Mayor</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <TextField
                  fullWidth
                  placeholder="Buscar producto…"
                  value={busquedaProducto}
                  onChange={(e) => setBusquedaProducto(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: FP.color.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2, ...fpInputSx }}
                />
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {cargando ? (
                  <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                    Cargando…
                  </Typography>
                ) : (
                  <Grid container spacing={1.2}>
                    {productosFiltrados.map((producto) => {
                      const esServicio =
                        String(producto.tipoproducto || '').toLowerCase() === 'servicio';
                      const enExistencia =
                        esServicio || parseFloat(producto.existencia) > 0;
                      return (
                        <Grid item xs={6} sm={4} key={producto.codigo}>
                          <Card
                            onClick={() => enExistencia && agregarProducto(producto)}
                            sx={{
                              height: '100%',
                              cursor: enExistencia && puedeModificar ? 'pointer' : 'not-allowed',
                              borderRadius: 2,
                              transition: 'all .15s ease',
                              bgcolor: enExistencia ? FP.color.cardBg : FP.color.dangerSoft,
                              border: `1px solid ${enExistencia ? FP.color.border : FP.color.danger}`,
                              boxShadow: 'none',
                              opacity: enExistencia ? 1 : 0.85,
                              '&:hover': enExistencia
                                ? {
                                    borderColor: FP.color.accentBlue,
                                    boxShadow: FP.shadow.soft,
                                    transform: 'translateY(-2px)',
                                  }
                                : {},
                            }}
                          >
                            <CardContent
                              sx={{
                                p: 1.5,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                '&:last-child': { pb: 1.5 },
                              }}
                            >
                              <Typography
                                sx={{
                                  color: FP.color.textMuted,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  letterSpacing: '0.04em',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {producto.codigo}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  mb: 0.5,
                                  color: FP.color.textPrimary,
                                  lineHeight: 1.3,
                                  fontSize: '0.85rem',
                                  minHeight: '2.2em',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {producto.descripcion}
                              </Typography>
                              <Typography
                                sx={{
                                  color: FP.color.navy,
                                  fontWeight: 800,
                                  fontSize: '1rem',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                ${parseFloat(producto.precio).toFixed(2)}
                              </Typography>
                              {tipoPrecio === 'mayor' && producto.precio_origen !== 'mayor' && (
                                <Typography
                                  sx={{ color: FP.color.warning, fontSize: '0.68rem', fontWeight: 600 }}
                                >
                                  Sin precio mayor: usa detalle
                                </Typography>
                              )}
                              <Typography
                                sx={{ color: FP.color.textSecondary, fontSize: '0.72rem' }}
                              >
                                ITBIS: {esEspecial ? 0 : producto.itbis}%
                              </Typography>
                              <Box sx={{ mt: 'auto', pt: 0.6, minHeight: 24 }}>
                                {esServicio ? (
                                  <Chip
                                    label="Servicio"
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.68rem',
                                      bgcolor: FP.color.surfaceAlt,
                                      color: FP.color.textSecondary,
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label={`Exist: ${parseFloat(producto.existencia).toFixed(0)}`}
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.68rem',
                                      bgcolor: enExistencia
                                        ? FP.color.successSoft
                                        : FP.color.dangerSoft,
                                      color: enExistencia
                                        ? FP.color.successDark
                                        : FP.color.danger,
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ ...cardShellSx, display: 'flex', flexDirection: 'column', minHeight: '600px' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 2.5 }}>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: FP.color.surfaceAlt,
                  border: `1px solid ${FP.color.border}`,
                  flexShrink: 0,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Documento fiscal"
                      value={tipoDocumento}
                      onChange={(e) => cambiarDocumentoFiscal(e.target.value)}
                      required
                      InputLabelProps={{ shrink: true }}
                      disabled={tiposDocumentosFiscales.length === 0 || !puedeModificar}
                      helperText={
                        tiposDocumentosFiscales.length === 0
                          ? 'No hay tipos de documentos configurados'
                          : 'Seleccione el tipo de documento'
                      }
                      sx={fpInputSx}
                    >
                      {tiposDocumentosFiscales.length === 0 ? (
                        <MenuItem value="">No hay tipos disponibles</MenuItem>
                      ) : (
                        tiposDocumentosFiscales.map((tipo) => (
                          <MenuItem key={tipo.nombre} value={tipo.nombre}>
                            {tipo.nombre}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sucursal"
                      value={sucursalActual || 'No definida'}
                      InputProps={{ readOnly: true }}
                      helperText="Sucursal del usuario actual"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  </Grid>
                  {(!esE32 || e32RequiereRnc) && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          freeSolo
                          options={opcionesRnc}
                          loading={cargandoRnc}
                          disabled={!puedeModificar}
                          value={rnc}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            const codigo = (option?.rnc || '').trim();
                            const nombre = (option?.descripcion || '').trim();
                            if (codigo && nombre) return `${codigo} — ${nombre}`;
                            return codigo || nombre || '';
                          }}
                          isOptionEqualToValue={(option, value) => {
                            const optRnc = typeof option === 'string' ? option : option?.rnc;
                            const valRnc = typeof value === 'string' ? value : value?.rnc;
                            return String(optRnc || '').trim() === String(valRnc || '').trim();
                          }}
                          filterOptions={(x) => x}
                          onInputChange={(_, value, reason) => {
                            if (reason === 'input' || reason === 'clear') {
                              setRnc(value || '');
                              if (busquedaRncTimerRef.current) {
                                clearTimeout(busquedaRncTimerRef.current);
                              }
                              busquedaRncTimerRef.current = setTimeout(() => {
                                buscarRncs(value || '');
                              }, 280);
                            }
                          }}
                          onChange={(_, option) => {
                            if (option && typeof option === 'object') {
                              setRnc((option.rnc || '').trim());
                              if ((option.descripcion || '').trim()) {
                                setRazonSocial(option.descripcion.trim());
                              }
                            } else if (typeof option === 'string') {
                              setRnc(option.trim());
                            } else if (option == null) {
                              setRnc('');
                            }
                          }}
                          renderOption={(props, option) => (
                            <li {...props} key={`${option.rnc}-${option.descripcion}`}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>
                                  {option.rnc}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: FP.color.textSecondary }}>
                                  {option.descripcion || '—'}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              required
                              label="RNC / Cédula"
                              helperText={
                                e32RequiereRnc
                                  ? 'Requerido: consumo de RD$250,000 o más (DGII)'
                                  : 'Busque en el catálogo RNC o escriba uno nuevo'
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={fpInputSx}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {cargandoRnc ? (
                                      <CircularProgress color="inherit" size={16} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label="Razón social / Nombre"
                          value={razonSocial}
                          onChange={(e) => setRazonSocial(e.target.value)}
                          disabled={!puedeModificar}
                          helperText={
                            e32RequiereRnc
                              ? 'Requerido: consumo de RD$250,000 o más (DGII)'
                              : 'Requerido para documentos distintos de Consumo (E32)'
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={fpInputSx}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Observaciones"
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      multiline
                      rows={2}
                      disabled={!puedeModificar}
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  minHeight: 0,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, pb: 1.2, borderBottom: `1px solid ${FP.color.border}` }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.2,
                      bgcolor: FP.color.accentBlueSoft,
                      color: FP.color.accentBlue,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ShoppingCartIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
                    Items de la factura
                  </Typography>
                </Stack>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: `1px solid ${FP.color.border}`,
                    borderRadius: 2,
                    overflow: 'auto',
                    mb: 2,
                    maxHeight: '400px',
                  }}
                >
                  <Table size="small" sx={itemsTableSx}>
                    {itemsColgroup}
                    <TableBody>
                      <TableRow
                        sx={{
                          '& td': {
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            border: 'none',
                            borderBottom: `2px solid ${FP.color.border}`,
                            bgcolor: FP.color.surfaceAlt,
                          },
                        }}
                      >
                        <TableCell sx={itemHeadLabelSx}>Código</TableCell>
                        <TableCell sx={itemHeadLabelSx}>Descripción</TableCell>
                        <TableCell align="center" sx={itemHeadLabelSx}>
                          Cant.
                        </TableCell>
                        <TableCell align="right" sx={itemHeadLabelSx}>
                          Precio
                        </TableCell>
                        <TableCell align="right" sx={itemHeadLabelSx}>
                          Descuento
                        </TableCell>
                        <TableCell align="right" sx={itemHeadLabelSx}>
                          Subtotal
                        </TableCell>
                        <TableCell align="center" sx={itemHeadLabelSx}>
                          Acción
                        </TableCell>
                      </TableRow>
                      {itemsFactura.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            align="center"
                            sx={{ color: FP.color.textSecondary, py: 4 }}
                          >
                            No hay items en la factura
                          </TableCell>
                        </TableRow>
                      ) : (
                        itemsFactura.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={item.codigo}
                            >
                              <Chip
                                label={item.codigo}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.72rem',
                                  bgcolor: FP.color.accentBlueSoft,
                                  color: FP.color.accentBlue,
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                color: FP.color.textPrimary,
                                fontSize: '0.85rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={item.descripcion}
                            >
                              {item.descripcion}
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.4,
                                  bgcolor: FP.color.surfaceAlt,
                                  borderRadius: 1.4,
                                  p: 0.4,
                                  width: 'fit-content',
                                  mx: 'auto',
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    actualizarCantidadItem(item.id, parseFloat(item.cantidad) - 1)
                                  }
                                  disabled={!puedeModificar}
                                  sx={{ color: FP.color.accentBlue }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography
                                  onClick={() => puedeModificar && abrirCalculadora(item)}
                                  sx={{
                                    minWidth: '40px',
                                    textAlign: 'center',
                                    cursor: puedeModificar ? 'pointer' : 'default',
                                    px: 1,
                                    fontWeight: 700,
                                    color: FP.color.textPrimary,
                                    fontSize: '0.88rem',
                                    fontVariantNumeric: 'tabular-nums',
                                    '&:hover': { color: FP.color.accentBlue },
                                  }}
                                >
                                  {parseFloat(item.cantidad).toFixed(0)}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    actualizarCantidadItem(item.id, parseFloat(item.cantidad) + 1)
                                  }
                                  disabled={!puedeModificar}
                                  sx={{ color: FP.color.accentBlue }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color: FP.color.textPrimary,
                                fontSize: '0.85rem',
                                fontVariantNumeric: 'tabular-nums',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              ${parseFloat(item.precio).toFixed(2)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {(() => {
                                const descLinea = parseFloat(item.descuento || 0);
                                const descGeneralItem = getDescuentoGeneralPorItem(item);
                                const descTotalItem = descLinea + descGeneralItem;
                                return descTotalItem > 0 ? (
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        color: FP.color.danger,
                                        fontSize: '0.85rem',
                                        fontVariantNumeric: 'tabular-nums',
                                      }}
                                    >
                                      -${descTotalItem.toFixed(2)}
                                    </Typography>
                                    {descGeneralItem > 0 && (
                                      <Typography
                                        sx={{
                                          color: FP.color.textMuted,
                                          display: 'block',
                                          fontSize: '0.7rem',
                                        }}
                                      >
                                        (general: -${descGeneralItem.toFixed(2)})
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography
                                    sx={{
                                      color: FP.color.textMuted,
                                      textAlign: 'right',
                                      fontSize: '0.85rem',
                                    }}
                                  >
                                    $0.00
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 800,
                                color: FP.color.navy,
                                fontSize: '0.9rem',
                                fontVariantNumeric: 'tabular-nums',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              ${(parseFloat(item.subtotal) - getDescuentoGeneralPorItem(item)).toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => eliminarItem(item.id)}
                                disabled={!puedeModificar}
                                sx={{ color: FP.color.danger }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ flexShrink: 0 }}>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: FP.color.surfaceAlt,
                    border: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                      Subtotal:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textPrimary,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      ${subtotalAntesDescuentos.toFixed(2)}
                    </Typography>
                  </Box>
                  {totalDescuentosLinea > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', pl: 1 }}>
                        Descuentos por línea:
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: FP.color.danger,
                          fontSize: '0.85rem',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        -${totalDescuentosLinea.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {descuentoGeneralTotal > 0 && reglaDescuentoGeneralAplicada && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', pl: 1 }}>
                        Descuento general ({reglaDescuentoGeneralAplicada.nombre_descuento}):
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: FP.color.danger,
                          fontSize: '0.85rem',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        -${descuentoGeneralTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                      Total descuentos:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: totalDescuentos > 0 ? FP.color.danger : FP.color.textMuted,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      -${totalDescuentos.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                      pb: 0.8,
                      borderBottom: `1px dashed ${FP.color.border}`,
                    }}
                  >
                    <Typography sx={{ color: FP.color.textPrimary, fontWeight: 600, fontSize: '0.88rem' }}>
                      Subtotal después de descuentos:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textPrimary,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      ${(subtotal - descuentoGeneralTotal).toFixed(2)}
                    </Typography>
                  </Box>
                  {!esEspecial && impuestosFactura.length > 0 ? (
                    <>
                      {impuestosFactura.map((impuesto) => (
                        <Box
                          key={impuesto.id}
                          sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
                        >
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', pl: 2 }}>
                            {impuesto.nombre}{' '}
                            {impuesto.porcentaje
                              ? `(${parseFloat(impuesto.porcentaje).toFixed(2)}%)`
                              : ''}
                            :
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: FP.color.textPrimary,
                              fontSize: '0.85rem',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            ${parseFloat(impuesto.monto || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      ))}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mt: 1,
                          pt: 0.8,
                          borderTop: `1px solid ${FP.color.border}`,
                        }}
                      >
                        <Typography sx={{ color: FP.color.textPrimary, fontWeight: 600, fontSize: '0.88rem' }}>
                          Total impuestos:
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: FP.color.textPrimary,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          ${totalImpuestos.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                        Impuestos:
                      </Typography>
                      <Typography sx={{ color: FP.color.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                        $0.00
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1.5, borderColor: FP.color.border }} />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.6,
                      borderRadius: 1.6,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Total</Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.4rem',
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      ${total.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.4 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={limpiarFactura}
                    disabled={!puedeModificar}
                    fullWidth
                    sx={{
                      py: 1.2,
                      textTransform: 'none',
                      borderRadius: 1.4,
                      fontWeight: 700,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
                    }}
                  >
                    Limpiar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={guardandoFactura ? undefined : <CheckCircleIcon />}
                    onClick={manejarGuardarFacturaClick}
                    fullWidth
                    disabled={itemsFactura.length === 0 || !puedeModificar || !usuarioLog || guardandoFactura}
                    sx={{
                      py: 1.2,
                      textTransform: 'none',
                      borderRadius: 1.4,
                      fontWeight: 700,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                      '&.Mui-disabled': guardandoFactura
                        ? { bgcolor: FP.color.navy, color: '#FFFFFF' }
                        : { bgcolor: FP.color.border, color: FP.color.textMuted },
                    }}
                  >
                    {guardandoFactura ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} color="inherit" />
                        Guardando...
                      </Box>
                    ) : (
                      'Guardar factura'
                    )}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ConsultaProducto
        open={dialogoConsultaProducto}
        onClose={() => setDialogoConsultaProducto(false)}
      />

      <PagoFactura
        open={dialogoPagoAbierto}
        total={total}
        puedeModificar={puedeModificar}
        formasPago={formasPagoDb}
        onClose={() => setDialogoPagoAbierto(false)}
        onConfirm={(datosPago) => {
          setDialogoPagoAbierto(false);
          setGuardandoFactura(true);
          guardarFactura(datosPago);
        }}
      />

      <VistaFactura
        open={vistaFacturaAbierta}
        onClose={() => setVistaFacturaAbierta(false)}
        facturaData={datosVistaFactura}
      />

      <Dialog
        open={calculadoraAbierta}
        onClose={cerrarCalculadora}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CalculateIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
              Cambiar cantidad
            </Typography>
            {itemEditando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                {itemEditando.descripcion}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <TextField
            fullWidth
            value={valorCalculadora}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                    Cantidad
                  </Typography>
                </InputAdornment>
              ),
              sx: {
                fontSize: '1.25rem',
                fontWeight: 700,
                bgcolor: FP.color.surfaceAlt,
                fontVariantNumeric: 'tabular-nums',
              },
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2.5, mt: 1, ...fpInputSx }}
          />
          <Grid container spacing={1}>
            {[
              ['7', '8', '9', 'C'],
              ['4', '5', '6', '←'],
              ['1', '2', '3', '='],
              ['0', '.', '', ''],
            ].map((row, rowIndex) => (
              <Grid container item spacing={1} key={rowIndex}>
                {row.map((val, colIndex) => (
                  <Grid item xs={3} key={colIndex}>
                    {val && (
                      <Button
                        fullWidth
                        variant={val === '=' ? 'contained' : 'outlined'}
                        onClick={() => manejarTeclaCalculadora(val)}
                        sx={{
                          minHeight: '58px',
                          fontSize: '1.15rem',
                          fontWeight: 700,
                          borderRadius: 1.4,
                          textTransform: 'none',
                          ...(val === '='
                            ? {
                                bgcolor: FP.color.navy,
                                color: '#FFFFFF',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                              }
                            : {
                                borderColor: FP.color.border,
                                color: FP.color.textPrimary,
                                bgcolor: '#FFFFFF',
                                '&:hover': {
                                  borderColor: FP.color.borderStrong,
                                  bgcolor: FP.color.surfaceAlt,
                                },
                              }),
                        }}
                      >
                        {val}
                      </Button>
                    )}
                  </Grid>
                ))}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Button
            onClick={cerrarCalculadora}
            startIcon={<CloseIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoCotizaciones}
        onClose={() => {
          setDialogoCotizaciones(false);
          setBusquedaCotizacion('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            maxHeight: '85vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FileUploadIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Importar cotización
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
              Busque por número o nombre de la cotización
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            placeholder="Buscar por número o nombre…"
            value={busquedaCotizacion}
            onChange={(e) => handleBuscarCotizacion(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DescriptionIcon sx={{ color: FP.color.textMuted }} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, ...fpInputSx }}
          />
          {cargandoCotizaciones ? (
            <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
              Cargando cotizaciones…
            </Typography>
          ) : cotizaciones.length === 0 ? (
            <Alert
              severity="info"
              sx={{ borderRadius: 2, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.infoSoft }}
            >
              {busquedaCotizacion
                ? 'No se encontraron cotizaciones con ese criterio de búsqueda.'
                : 'No hay cotizaciones disponibles. Realice una búsqueda para encontrar cotizaciones.'}
            </Alert>
          ) : (
            <TableContainer
              sx={{
                maxHeight: '420px',
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={cellHeadSx}>Número</TableCell>
                    <TableCell sx={cellHeadSx}>Nombre</TableCell>
                    <TableCell sx={cellHeadSx}>Cliente</TableCell>
                    <TableCell sx={cellHeadSx} align="center">
                      Fecha
                    </TableCell>
                    <TableCell sx={cellHeadSx} align="right">
                      Total
                    </TableCell>
                    <TableCell sx={cellHeadSx} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cotizaciones.map((cotizacion) => (
                    <TableRow key={cotizacion.id} hover>
                      <TableCell
                        sx={{ color: FP.color.textPrimary, fontWeight: 700, fontSize: '0.85rem' }}
                      >
                        {cotizacion.numero}
                      </TableCell>
                      <TableCell sx={{ color: FP.color.textPrimary, fontSize: '0.85rem' }}>
                        {cotizacion.nombre}
                      </TableCell>
                      <TableCell sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                        {cotizacion.cliente || '-'}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}
                      >
                        {cotizacion.fecha
                          ? new Date(cotizacion.fecha).toLocaleDateString('es-ES')
                          : '-'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: FP.color.textPrimary,
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        ${parseFloat(cotizacion.total || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<FileUploadIcon />}
                          onClick={() => importarCotizacion(cotizacion.id)}
                          disabled={!puedeModificar || !usuarioLog}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 1.2,
                            fontWeight: 700,
                            bgcolor: FP.color.navy,
                            color: '#FFFFFF',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
                          }}
                        >
                          Importar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Button
            onClick={() => {
              setDialogoCotizaciones(false);
              setBusquedaCotizacion('');
            }}
            startIcon={<CloseIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacturaPOS;
